import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import eventBus from "src/common/eventBus";
import { Button } from "src/components";
import {
  Form,
  FormDatePicker,
  FormNumber,
  FormText,
  FormSelect,
  FormCheckBox,
} from "src/components/Form";
import api from "src/config/api";
import "./ContractStyle.css";
import ContractResponsibilities from "./ContractResponsibilities";

const CreateContract = () => {
  const navigate = useNavigate();
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBike, setSelectedBike] = useState(null);
  const [bikeMap, setBikeMap] = useState({});
  const sigPad = useRef(null);
  const contractRef = useRef(null);

  useEffect(() => {
    const fetchBikes = async () => {
      try {
        const response = await api.get("bikes", { params: { limit: 100, status: "IN" } });
        if (response.data && response.data.data) {
          const bikeData = response.data.data;
          const bikeOptions = bikeData.map((bike) => ({
            label: `${bike.bikeName} (${bike.licenseNumber || "No License"})`,
            value: bike.id,
          }));

          // Create a map of bike IDs to bike objects for quick lookup
          const bikeLookup = {};
          bikeData.forEach((bike) => {
            bikeLookup[bike.id] = bike;
          });

          setBikes(bikeOptions);
          setBikeMap(bikeLookup);
        }
      } catch (error) {
        console.error("Error fetching bikes:", error);
        eventBus.emit("dialog", {
          type: "error",
          message: "Failed to load bikes",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBikes();
  }, []);

  const handleSubmit = async (data) => {
    try {
      // Add signature data if exists
      if (sigPad.current && !sigPad.current.isEmpty()) {
        data.signature = sigPad.current.toDataURL();
      }

      const response = await api.post("contracts", data);

      if (response.data.error) {
        throw new Error(response);
      }
      eventBus.emit("dialog", {
        type: "success",
        message: "Contract created successfully!",
      });

      setTimeout(() => {
        navigate("/contracts");
        eventBus.emit("dialog", {
          open: false,
        });
      }, 2000);
    } catch (error) {
      console.log(error);
      eventBus.emit("dialog", {
        type: "error",
        message: error.data?.message || "Something wrong",
      });
    }
  };

  const clearSignature = () => {
    if (sigPad.current) {
      sigPad.current.clear();
    }
  };

  const handleBikeChange = (bikeId) => {
    setSelectedBike(bikeMap[bikeId]);
  };

  if (loading) {
    return <Spin tip="Loading bikes..." />;
  }

  return (
    <div className="contract-container" ref={contractRef}>
      <div className="contract-header">
        <div className="logo">Just Bikes</div>
        <div className="subtitle">Motorbike, bicycle rental & tours</div>
        <div className="contact">Phone/WhatsApp: +84 77 7507 323</div>
        <div className="hours">OPEN 9AM TO 5PM</div>
      </div>

      <h2 className="contract-title">Rental Contract</h2>

      <Form
        onSubmit={handleSubmit}
        layout="horizontal"
        className="contract-form"
      >
        <div className="form-row">
          <div className="form-label">Name:</div>
          <FormText
            name="customerName"
            placeholder=""
            noLabel
            className="form-input"
          />
        </div>

        <div className="form-row">
          <div className="form-label">Nationality:</div>
          <FormText
            name="nationality"
            placeholder=""
            noLabel
            className="form-input"
          />
        </div>

        <div className="form-row">
          <div className="form-label">Phone/WhatsApp:</div>
          <FormText
            name="phoneWhatsapp"
            placeholder=""
            noLabel
            className="form-input"
          />
        </div>

        <div className="form-row">
          <div className="form-label">Address/Hotel:</div>
          <FormText
            name="addressHotel"
            placeholder=""
            noLabel
            className="form-input"
          />
        </div>

        <div className="form-row bike-selection">
          <div className="form-label">Bike:</div>
          <FormSelect
            name="bikeId"
            placeholder="Select a bike"
            options={bikes}
            required
            noLabel
            className="form-input"
            onChange={handleBikeChange}
          />
        </div>

        <div className="form-row">
          <div className="form-label">Number:</div>
          <div className="form-input license-display">
            {selectedBike?.licenseNumber || ""}
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">Start date and time:</div>
          <FormDatePicker
            placeholder=""
            format="DD/MM/YYYY HH:mm"
            showTime
            name="startDate"
            noLabel
            className="form-input"
          />
        </div>

        <div className="form-row">
          <div className="form-label">End date and time:</div>
          <FormDatePicker
            placeholder=""
            format="DD/MM/YYYY HH:mm"
            showTime
            name="endDate"
            noLabel
            className="form-input"
          />
        </div>

        <div className="form-row">
          <div className="form-label">Price:</div>
          <FormNumber
            placeholder=""
            name="price"
            noLabel
            className="form-input"
          />
        </div>

        <div className="form-row">
          <div className="form-label">Deposit:</div>
          <FormNumber
            placeholder=""
            name="deposit"
            noLabel
            className="form-input"
          />
        </div>
        <div className="form-row">
          <div className="form-label">Deposit Type:</div>
          <div className="deposit-options">
            <FormCheckBox
              name="depositPassport"
              label="Passport"
              defaultValue={false}
              noLabel
            />
            <FormCheckBox
              name="depositIDCard"
              label="ID card"
              defaultValue={false}
              noLabel
            />
            <FormCheckBox
              name="depositDrivingLicense"
              label="Driving license"
              defaultValue={false}
              noLabel
            />
            <FormCheckBox
              name="depositCash"
              label="Cash"
              defaultValue={false}
              noLabel
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">Helmets:</div>
          <div className="helmet-inputs">
            <div className="helmet-input">
              <span className="helmet-label">Large</span>
              <FormNumber
                name="helmetLarge"
                placeholder=""
                noLabel
                defaultValue={0}
                min={0}
                className="qty-input"
              />
            </div>
            <div className="helmet-input">
              <span className="helmet-label">Small</span>
              <FormNumber
                name="helmetSmall"
                placeholder=""
                noLabel
                defaultValue={0}
                min={0}
                className="qty-input"
              />
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-label">Extras:</div>
          <div className="extras-options">
            <div className="extras-row"></div>
            <div className="extras-row">
              <div className="checkbox-item">
                <FormCheckBox
                  name="phoneHolder"
                  label="Phone holder"
                  defaultValue={false}
                  noLabel
                />
              </div>
              <div className="checkbox-item">
                <FormCheckBox
                  name="bicycleLock"
                  label="Bicycle lock"
                  defaultValue={false}
                  noLabel
                />
              </div>
              <div className="checkbox-item">
                <FormCheckBox
                  name="bicycleLights"
                  label="Bicycle lights"
                  defaultValue={false}
                  noLabel
                />
              </div>
            </div>
          </div>
        </div>

        <ContractResponsibilities />

        <div className="signature-section">
          <div className="signature-container">
            <div className="signature-label">Signature:</div>
            <div className="signature-pad-container">
              <SignatureCanvas
                ref={sigPad}
                penColor="black"
                canvasProps={{
                  className: "signature-pad",
                  width: 300,
                  height: 100,
                }}
              />
            </div>
            <Button type="default" onClick={clearSignature} size="small">
              Clear Signature
            </Button>
          </div>

          <div className="date-container">
            <div className="date-label">Date:</div>
            <div className="date-value">{new Date().toLocaleDateString()}</div>
          </div>
        </div>

        <div className="contract-footer">
          <Button
            type="primary"
            htmlType="submit"
            className="submit-button"
          >
            Create Contract
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateContract;
